import React from 'react';
import { Link } from 'react-router-dom';
import {
  IconPlayerPlay,
  IconNews,
  IconTrophy,
  IconUsers,
  IconStar,
  IconTarget,
  IconArrowRight,
  IconMedal,
  IconChartLine
} from '@tabler/icons-react';
import './UBUnited.css';

const UBUnited = () => {
  const features = [
    {
      icon: <IconPlayerPlay size={32} stroke={1.5} />,
      title: 'Pro-Level Video Training',
      description: 'Learn from Olympic medalists, professional athletes, and certified coaches through comprehensive video courses.'
    },
    {
      icon: <IconNews size={32} stroke={1.5} />,
      title: 'Curated Sports News',
      description: 'Stay informed with sport-specific news aggregated and curated by athletes who understand what matters.'
    },
    {
      icon: <IconTarget size={32} stroke={1.5} />,
      title: 'Skill Development Paths',
      description: 'Structured learning paths that take you from fundamentals to advanced techniques in any sport.'
    },
    {
      icon: <IconChartLine size={32} stroke={1.5} />,
      title: 'Progress Tracking',
      description: 'Track your improvement over time with personalized metrics and achievement milestones.'
    }
  ];

  const sports = [
    'Pickleball', 'Tennis', 'Basketball', 'Soccer',
    'Volleyball', 'Badminton', 'Padel', 'Squash'
  ];

  const athleteTypes = [
    {
      icon: <IconMedal size={40} stroke={1.5} />,
      title: 'Olympic Athletes',
      description: 'Learn techniques that won medals on the world stage'
    },
    {
      icon: <IconTrophy size={40} stroke={1.5} />,
      title: 'Professional Players',
      description: 'Insights from those who compete at the highest levels'
    },
    {
      icon: <IconStar size={40} stroke={1.5} />,
      title: 'Certified Coaches',
      description: 'Structured instruction from experienced professionals'
    }
  ];

  return (
    <main className="ubu-page">
      {/* Hero Section */}
      <section className="ubu-hero">
        <div className="ubu-hero-content">
          <span className="ubu-hero-badge">For Athletes, By Athletes</span>
          <h1 className="ubu-hero-title">
            Master Any Sport with<br />
            <span className="ubu-gradient-text">UBUnited</span>
          </h1>
          <p className="ubu-hero-description">
            Premium video resources, curated news, and expert instruction from Olympic
            and professional athletes. Everything you need to elevate your game,
            no matter which sport you play.
          </p>
          <div className="ubu-hero-cta">
            <span className="ubu-coming-soon-badge">Portal Coming Soon</span>
          </div>
        </div>
      </section>

      {/* What is UBUnited */}
      <section className="ubu-intro">
        <div className="ubu-container">
          <div className="ubu-intro-content">
            <h2 className="ubu-section-title">What is UBUnited?</h2>
            <p className="ubu-intro-text">
              UBUnited is the athlete-focused side of UBSports. While our software solutions
              serve leagues and organizations, UBUnited serves <strong>you</strong>—the athlete.
              We bring together world-class instruction, sport-specific content, and a community
              of passionate players all in one place.
            </p>
            <p className="ubu-intro-text">
              Our content is created and curated by people who have competed at the highest levels.
              Olympic medalists, professional players, and certified coaches contribute their
              expertise to help athletes of all skill levels reach their potential.
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="ubu-features">
        <div className="ubu-container">
          <h2 className="ubu-section-title">What You'll Get</h2>
          <div className="ubu-features-grid">
            {features.map((feature, index) => (
              <article key={index} className="ubu-feature-card">
                <div className="ubu-feature-icon">{feature.icon}</div>
                <h3 className="ubu-feature-title">{feature.title}</h3>
                <p className="ubu-feature-description">{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Expert Contributors */}
      <section className="ubu-experts">
        <div className="ubu-container">
          <h2 className="ubu-section-title">Learn From the Best</h2>
          <p className="ubu-section-subtitle">
            Our content creators and curators have competed and coached at elite levels
          </p>
          <div className="ubu-experts-grid">
            {athleteTypes.map((type, index) => (
              <article key={index} className="ubu-expert-card">
                <div className="ubu-expert-icon">{type.icon}</div>
                <h3 className="ubu-expert-title">{type.title}</h3>
                <p className="ubu-expert-description">{type.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Sports Covered */}
      <section className="ubu-sports">
        <div className="ubu-container">
          <h2 className="ubu-section-title">Sports We Cover</h2>
          <p className="ubu-section-subtitle">
            Comprehensive resources across racquet sports and beyond
          </p>
          <div className="ubu-sports-list">
            {sports.map((sport, index) => (
              <span key={index} className="ubu-sport-tag">{sport}</span>
            ))}
          </div>
          <p className="ubu-sports-note">More sports coming soon</p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="ubu-cta">
        <div className="ubu-container">
          <div className="ubu-cta-content">
            <h2 className="ubu-cta-title">The UBUnited Portal is Coming Soon</h2>
            <p className="ubu-cta-description">
              We're building the ultimate athlete resource platform. In the meantime,
              check out our league management system to find and join competitive play.
            </p>
            <div className="ubu-cta-buttons">
              <Link to="/leagues" className="ubu-btn-primary">
                <span>Explore Leagues</span>
                <IconArrowRight size={18} />
              </Link>
              <Link to="/software" className="ubu-btn-secondary">
                Learn About Our Software
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default UBUnited;
